import { Check, X } from 'lucide-react';
import type { Plan, PlanFeaturePoint } from '../api';

function weeksLabel(n: number) {
  return n === 1 ? '1 week' : `${n} weeks`;
}

function PointMark({ positive }: { positive: boolean }) {
  return positive
    ? <Check className="w-4 h-4 text-success shrink-0" aria-hidden />
    : <X className="w-4 h-4 text-danger shrink-0" aria-hidden />;
}

/** Built-in benefits + shared points for all plans + plan-specific points. */
export function PlanBenefitList({
  plan,
  sharedPoints = [],
  variant = 'checks',
}: {
  plan: Plan;
  sharedPoints?: PlanFeaturePoint[] | { text: string }[];
  variant?: 'checks' | 'rows';
}) {
  const custom = plan.points ?? [];
  const shared = sharedPoints.map(p => p.text);
  const clinics = plan.showClinics === false ? [] : (plan.clinics ?? []).filter(c => c.name);

  if (variant === 'rows') {
    return (
      <div className="mt-auto space-y-2 text-sm text-ink-soft border-t border-line pt-4">
        <div className="flex justify-between"><span>Clinic weeks</span><span className="font-medium text-ink">{plan.internshipWeeksIncluded}</span></div>
        <div className="flex justify-between"><span>Mentorship questions to doctors</span><span className="font-medium text-ink">{plan.consultationCreditsIncluded}</span></div>
        <div className="flex justify-between"><span>How long</span><span className="font-medium text-ink">{weeksLabel(plan.durationWeeks)} from your first clinic week</span></div>
        {clinics.length > 0 && (
          <div className="pt-1">
            <div className="text-ink-soft mb-1.5">Clinics</div>
            <ul className="space-y-1.5">
              {clinics.map(c => (
                <li key={c.id} className="flex items-start gap-2 font-medium text-ink">
                  <span className="mt-[0.45rem] w-1.5 h-1.5 rounded-full bg-sand shrink-0" aria-hidden />
                  <span>{c.name}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {shared.map((text, i) => (
          <div key={`s-${i}`} className="flex justify-between gap-3">
            <span>{text}</span>
            <Check className="w-4 h-4 text-success shrink-0" />
          </div>
        ))}
        {custom.map((point, i) => (
          <div key={`p-${i}`} className="flex justify-between gap-3">
            <span>{point.text}</span>
            <PointMark positive={point.positive !== false} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="mt-auto space-y-2.5 text-sm text-ink-soft border-t border-line pt-4 mb-5 sm:mb-6">
      <div className="flex items-center gap-2"><Check className="w-4 h-4 text-success shrink-0" />{plan.internshipWeeksIncluded} clinic weeks</div>
      <div className="flex items-center gap-2"><Check className="w-4 h-4 text-success shrink-0" />{plan.consultationCreditsIncluded} mentorship questions to doctors</div>
      <div className="flex items-center gap-2"><Check className="w-4 h-4 text-success shrink-0" />{weeksLabel(plan.durationWeeks)} from your first clinic week</div>
      {clinics.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <Check className="w-4 h-4 text-success shrink-0" />
            <span>Intern at</span>
          </div>
          <ul className="space-y-1.5 pl-6">
            {clinics.map(c => (
              <li key={c.id} className="flex items-start gap-2 text-ink">
                <span className="mt-[0.45rem] w-1.5 h-1.5 rounded-full bg-sand shrink-0" aria-hidden />
                <span>{c.name}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {shared.map((text, i) => (
        <div key={`s-${i}`} className="flex items-center gap-2">
          <Check className="w-4 h-4 text-success shrink-0" />
          {text}
        </div>
      ))}
      {custom.map((point, i) => (
        <div key={`p-${i}`} className="flex items-center gap-2">
          <PointMark positive={point.positive !== false} />
          {point.text}
        </div>
      ))}
    </div>
  );
}
